import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { login } from "@/api/auth";

// ============== ICONS DEFINITIONS ==============
const Icons = {
  Email: () => <span>📧</span>,
  Lock: () => <span>🔒</span>,
  Eye: () => <span>👁️</span>,
  EyeSlash: () => <span>👁️‍🗨️</span>,
  ArrowRight: () => <span>→</span>,
  User: () => <span>👤</span>,
  Home: () => <span>🏠</span>,
  Graduation: () => <span>🎓</span>,
  Shield: () => <span>🛡️</span>,
};

// ============== CSS STYLES ==============
const styles = `
    /* ============== CSS Variables ============== */
    :root {
      --primary: #6a11cb;
      --primary-light: #8a2be2;
      --primary-dark: #4a00e0;
      --secondary: #00dbde;
      --secondary-light: #00c6ff;
      --accent: #ff6b6b;
      --accent-light: #ff9e7d;
      --accent-dark: #ff4757;
      --dark: #1a1a2e;
      --dark-light: #16213e;
      --light: #f8f9fa;
      --light-gray: #e9ecef;
      --gray: #6c757d;
      --success: #00b894;
      --warning: #fdcb6e;
      --danger: #ff7675;
      --info: #00cec9;
      
      --gradient-primary: linear-gradient(135deg, var(--primary), var(--primary-light), var(--secondary));
      --gradient-secondary: linear-gradient(135deg, var(--secondary), var(--secondary-light), #36d1dc);
      --gradient-accent: linear-gradient(135deg, var(--accent), var(--accent-light), #ff9a9e);
      --gradient-dark: linear-gradient(135deg, var(--dark), var(--dark-light), #0f3460);
      
      --border-radius-xl: 25px;
      --border-radius-lg: 20px;
      --border-radius-md: 15px;
      --border-radius-sm: 10px;
      
      --box-shadow-3d: 15px 15px 30px rgba(0, 0, 0, 0.15), -5px -5px 20px rgba(255, 255, 255, 0.7);
      --box-shadow-lg: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
      --box-shadow-md: 0 20px 40px rgba(0, 0, 0, 0.1);
      --box-shadow-sm: 0 10px 30px rgba(0, 0, 0, 0.08);
      --box-shadow-hover: 0 35px 60px rgba(106, 17, 203, 0.15);
      --box-shadow-neon: 0 0 20px rgba(106, 17, 203, 0.7);
      
      --transition-smooth: all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
      --transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
      --transition-fast: all 0.3s ease;
      
      --glass-bg: rgba(255, 255, 255, 0.12);
      --glass-border: rgba(255, 255, 255, 0.25);
      --glass-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
      
      --neon-primary: 0 0 20px rgba(106, 17, 203, 0.8);
      --neon-secondary: 0 0 20px rgba(0, 219, 222, 0.8);
      --neon-accent: 0 0 20px rgba(255, 107, 107, 0.8);
    }

    /* ============== Reset & Base ============== */
    .login-page {
      font-family: 'Cairo', 'Tajawal', 'Poppins', 'Segoe UI', sans-serif;
      line-height: 1.8;
      color: var(--dark);
      background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 50%, #f1f3ff 100%);
      direction: rtl;
      overflow-x: hidden;
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
    }

    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    html {
      scroll-behavior: smooth;
    }

    /* ============== Animations ============== */
    @keyframes fadeIn {
      from { 
        opacity: 0;
        transform: translateY(30px);
      }
      to { 
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-20px); }
    }

    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.8; }
    }

    @keyframes shimmer {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }

    @keyframes rotate {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    /* ============== Login Container ============== */
    .login-container {
      width: 100%;
      max-width: 1200px;
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 50px;
      align-items: center;
      animation: fadeIn 1s ease;
    }

    @media (max-width: 992px) {
      .login-container {
        grid-template-columns: 1fr;
        gap: 40px;
      }
    }

    /* ============== Login Info ============== */
    .login-info {
      text-align: right;
    }

    .login-logo {
      display: flex;
      align-items: center;
      gap: 20px;
      margin-bottom: 40px;
    }

    .logo-icon {
      width: 80px;
      height: 80px;
      background: var(--gradient-primary);
      border-radius: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 36px;
      box-shadow: var(--box-shadow-neon);
      animation: float 6s ease-in-out infinite;
    }

    .logo-text {
      font-size: 2.5rem;
      font-weight: 900;
      background: linear-gradient(135deg, var(--primary), var(--primary-light), #9d4edd);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      letter-spacing: -0.5px;
      text-shadow: 2px 2px 4px rgba(0,0,0,0.1);
    }

    .login-title {
      font-size: 3.5rem;
      font-weight: 900;
      margin-bottom: 25px;
      background: linear-gradient(135deg, var(--dark), var(--primary));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      line-height: 1.2;
    }

    .login-subtitle {
      font-size: 1.3rem;
      color: var(--gray);
      margin-bottom: 40px;
      line-height: 1.8;
      max-width: 500px;
    }

    .features-list {
      list-style: none;
      margin-top: 40px;
    }

    .features-list li {
      display: flex;
      align-items: center;
      gap: 15px;
      margin-bottom: 25px;
      color: var(--dark);
      font-size: 1.1rem;
      font-weight: 600;
      padding: 15px 25px;
      border-radius: var(--border-radius-lg);
      background: rgba(106, 17, 203, 0.05);
      transition: var(--transition-smooth);
    }

    .features-list li:hover {
      background: rgba(106, 17, 203, 0.1);
      transform: translateX(-10px);
    }

    .feature-icon {
      width: 40px;
      height: 40px;
      background: var(--gradient-primary);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 1.2rem;
      flex-shrink: 0;
    }

    /* ============== Login Form ============== */
    .login-form-container {
      background: white;
      padding: 60px 50px;
      border-radius: var(--border-radius-xl);
      box-shadow: var(--box-shadow-lg);
      position: relative;
      overflow: hidden;
      border: 3px solid transparent;
      transition: var(--transition-smooth);
    }

    .login-form-container:hover {
      border-color: var(--primary);
      box-shadow: var(--box-shadow-hover);
    }

    .login-form-container::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 100%;
      height: 5px;
      background: var(--gradient-primary);
      box-shadow: var(--neon-primary);
    }

    .form-header {
      text-align: center;
      margin-bottom: 40px;
    }

    .form-title {
      font-size: 2.2rem;
      font-weight: 900;
      margin-bottom: 15px;
      background: linear-gradient(135deg, var(--dark), var(--primary));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .form-subtitle {
      color: var(--gray);
      font-size: 1.1rem;
    }

    /* ============== Form Elements ============== */
    .form-group {
      margin-bottom: 30px;
      position: relative;
    }

    .form-label {
      display: block;
      margin-bottom: 12px;
      font-weight: 700;
      color: var(--dark);
      font-size: 1.1rem;
      padding-right: 5px;
    }

    .input-with-icon {
      position: relative;
    }

    .input-icon {
      position: absolute;
      top: 50%;
      right: 20px;
      transform: translateY(-50%);
      color: var(--gray);
      font-size: 1.2rem;
      z-index: 2;
    }

    .form-input {
      width: 100%;
      padding: 20px 60px 20px 20px;
      border: 3px solid var(--light-gray);
      border-radius: var(--border-radius-lg);
      font-size: 1.1rem;
      transition: var(--transition-smooth);
      background: white;
      font-family: 'Cairo', 'Tajawal', sans-serif;
    }

    .form-input:focus {
      outline: none;
      border-color: var(--primary);
      box-shadow: 0 0 0 6px rgba(106, 17, 203, 0.1);
      transform: translateY(-3px);
    }

    .password-toggle {
      position: absolute;
      top: 50%;
      left: 20px;
      transform: translateY(-50%);
      background: none;
      border: none;
      color: var(--gray);
      cursor: pointer;
      font-size: 1.2rem;
      transition: var(--transition-fast);
      z-index: 2;
    }

    .password-toggle:hover {
      color: var(--primary);
      transform: translateY(-50%) scale(1.1);
    }

    /* ============== Buttons ============== */
    .btn {
      padding: 18px 42px;
      border-radius: var(--border-radius-xl);
      font-size: 1.15rem;
      font-weight: 800;
      cursor: pointer;
      transition: var(--transition-smooth);
      border: none;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 15px;
      text-decoration: none;
      position: relative;
      overflow: hidden;
      z-index: 1;
      letter-spacing: 0.5px;
    }

    .btn:hover {
      transform: translateY(-5px);
    }

    .btn-primary {
      background: var(--gradient-primary);
      color: white;
      box-shadow: var(--box-shadow-3d), var(--neon-primary);
      width: 100%;
    }

    .btn-primary:hover {
      box-shadow: var(--box-shadow-hover), 0 0 40px rgba(106, 17, 203, 0.6);
    }

    .btn-primary.loading {
      opacity: 0.8;
      cursor: not-allowed;
    }

    .btn-primary.loading::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
      animation: shimmer 1.5s infinite;
    }

    .btn-outline {
      background: transparent;
      color: var(--primary);
      border: 3px solid var(--primary);
      position: relative;
      overflow: hidden;
      box-shadow: var(--box-shadow-sm);
      width: 100%;
    }

    .btn-outline:hover {
      color: white;
      border-color: transparent;
    }

    .btn-outline::before {
      content: '';
      position: absolute;
      top: 50%;
      right: 50%;
      width: 0;
      height: 0;
      background: var(--gradient-primary);
      border-radius: 50%;
      transform: translate(50%, -50%);
      transition: width 0.8s cubic-bezier(0.34, 1.56, 0.64, 1), height 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
      z-index: -1;
    }

    .btn-outline:hover::before {
      width: 400%;
      height: 400%;
    }

    /* ============== Error Message ============== */
    .error-message {
      background: rgba(255, 107, 107, 0.1);
      border: 2px solid var(--accent);
      color: var(--accent-dark);
      padding: 20px;
      border-radius: var(--border-radius-md);
      margin-top: 25px;
      text-align: center;
      font-weight: 600;
      animation: fadeIn 0.3s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
    }

    .success-message {
      background: rgba(0, 184, 148, 0.1);
      border: 2px solid var(--success);
      color: var(--success);
      padding: 20px;
      border-radius: var(--border-radius-md);
      margin-top: 25px;
      text-align: center;
      font-weight: 600;
      animation: fadeIn 0.3s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
    }

    /* ============== Form Footer ============== */
    .form-footer {
      margin-top: 30px;
      text-align: center;
      color: var(--gray);
    }

    .form-footer a {
      color: var(--primary);
      text-decoration: none;
      font-weight: 700;
      transition: var(--transition-fast);
    }

    .form-footer a:hover {
      color: var(--primary-dark);
      text-decoration: underline;
    }

    .divider {
      display: flex;
      align-items: center;
      margin: 30px 0;
      color: var(--gray);
    }

    .divider::before,
    .divider::after {
      content: '';
      flex: 1;
      height: 2px;
      background: var(--light-gray);
    }

    .divider-text {
      padding: 0 15px;
      font-size: 0.9rem;
    }

    /* ============== Floating Elements ============== */
    .floating-elements {
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      right: 0;
      pointer-events: none;
      z-index: 1;
    }

    .floating-element {
      position: absolute;
      width: 60px;
      height: 60px;
      background: var(--gradient-primary);
      border-radius: 15px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 1.5rem;
      box-shadow: var(--box-shadow-neon);
      animation: float 8s ease-in-out infinite;
    }

    .floating-element:nth-child(1) {
      top: 10%;
      right: 10%;
      animation-delay: 0s;
    }

    .floating-element:nth-child(2) {
      top: 70%;
      right: 15%;
      animation-delay: 2s;
      background: var(--gradient-secondary);
    }

    .floating-element:nth-child(3) {
      top: 40%;
      left: 10%;
      animation-delay: 4s;
      background: var(--gradient-accent);
    }

    /* ============== Back Home ============== */
    .back-home {
      position: absolute;
      top: 30px;
      right: 30px;
      display: inline-flex;
      align-items: center;
      gap: 10px;
      color: var(--dark);
      text-decoration: none;
      font-size: 1.1rem;
      font-weight: 600;
      padding: 12px 25px;
      background: rgba(255, 255, 255, 0.9);
      border-radius: var(--border-radius-lg);
      box-shadow: var(--box-shadow-sm);
      transition: var(--transition-smooth);
      z-index: 100;
    }

    .back-home:hover {
      background: white;
      transform: translateY(-3px);
      box-shadow: var(--box-shadow-md);
    }

    /* ============== Responsive ============== */
    @media (max-width: 768px) {
      .login-page {
        padding: 20px 15px;
      }
      
      .login-container {
        gap: 30px;
      }
      
      .login-form-container {
        padding: 40px 30px;
      }
      
      .login-title {
        font-size: 2.5rem;
      }
      
      .logo-text {
        font-size: 2rem;
      }
      
      .logo-icon {
        width: 60px;
        height: 60px;
        font-size: 28px;
      }
      
      .form-title {
        font-size: 1.8rem;
      }
      
      .btn {
        padding: 16px 32px;
        font-size: 1rem;
      }
    }

    @media (max-width: 480px) {
      .login-form-container {
        padding: 30px 20px;
      }
      
      .login-title {
        font-size: 2rem;
      }
      
      .features-list li {
        padding: 12px 20px;
        font-size: 1rem;
      }
      
      .form-input {
        padding: 16px 50px 16px 16px;
      }
    }
  `;

// ============== COMPONENT ==============
export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    // Basic validation
    if (!email || !password) {
      setError("الرجاء ملء جميع الحقول");
      setLoading(false);
      return;
    }

    try {
      // تسجيل الدخول (Laravel يتحقق من role = admin)
      await login({ email, password });

      setSuccess("تم تسجيل الدخول بنجاح! يتم تحويلك...");
      
      // التحويل إلى لوحة الأدمن بعد تأخير بسيط
      setTimeout(() => {
        navigate("/admin", { replace: true });
      }, 1500);

    } catch (err) {
      setError("بيانات الدخول غير صحيحة أو ليس لديك صلاحية أدمن");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <style>{styles}</style>

      {/* Back Home Link */}
      <a href="/" className="back-home">
        <Icons.Home />
        <span>العودة للرئيسية</span>
      </a>

      <div className="login-container">
        {/* Login Info Section */}
        <div className="login-info">
          <div className="login-logo">
            <div className="logo-icon">
              <Icons.Shield />
            </div>
            <div className="logo-text">لوحة التحكم</div>
          </div>

          <h1 className="login-title">تسجيل دخول المشرفين</h1>
          
          <p className="login-subtitle">
            قم بتسجيل الدخول باستخدام بيانات حساب المشرف للوصول إلى لوحة التحكم
            وإدارة النظام بالكامل.
          </p>

          <ul className="features-list">
            <li>
              <div className="feature-icon">
                <Icons.Shield />
              </div>
              <span>وصول آمن ومشفر</span>
            </li>
            <li>
              <div className="feature-icon">
                <Icons.Graduation />
              </div>
              <span>إدارة الأكاديميات والدورات</span>
            </li>
            <li>
              <div className="feature-icon">
                <Icons.User />
              </div>
              <span>مراقبة الطلاب والمدرّبين</span>
            </li>
          </ul>
        </div>

        {/* Login Form Section */}
        <div className="login-form-container">
          {/* Floating Elements */}
          <div className="floating-elements">
            <div className="floating-element">
              <Icons.Shield />
            </div>
            <div className="floating-element">
              <Icons.User />
            </div>
            <div className="floating-element">
              <Icons.Lock />
            </div>
          </div>

          <div className="form-header">
            <h2 className="form-title">مرحباً بعودتك</h2>
            <p className="form-subtitle">أدخل بيانات حساب المشرف للدخول</p>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Email Input */}
            <div className="form-group">
              <label className="form-label">البريد الإلكتروني</label>
              <div className="input-with-icon">
                <input
                  type="email"
                  className="form-input"
                  placeholder="admin@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  dir="rtl"
                />
                <div className="input-icon">
                  <Icons.Email />
                </div>
              </div>
            </div>

            {/* Password Input */}
            <div className="form-group">
              <label className="form-label">كلمة المرور</label>
              <div className="input-with-icon">
                <input
                  type={showPassword ? "text" : "password"}
                  className="form-input"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  dir="rtl"
                />
                <div className="input-icon">
                  <Icons.Lock />
                </div>
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                >
                  {showPassword ? <Icons.EyeSlash /> : <Icons.Eye />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className={`btn btn-primary ${loading ? 'loading' : ''}`}
              disabled={loading}
            >
              {loading ? "جاري تسجيل الدخول..." : "تسجيل الدخول"}
              <Icons.ArrowRight />
            </button>

            {/* Error Message */}
            {error && (
              <div className="error-message">
                <Icons.Shield />
                <span>{error}</span>
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="success-message">
                <Icons.Shield />
                <span>{success}</span>
              </div>
            )}

            {/* Divider */}
            <div className="divider">
              <div className="divider-text">أو</div>
            </div>

            {/* Alternative Login Option */}
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => navigate("/")}
              disabled={loading}
            >
              <Icons.Home />
              <span>العودة للموقع الرئيسي</span>
            </button>
          </form>

          {/* Form Footer */}
          <div className="form-footer">
            <p>نسيت كلمة المرور؟ <a href="/forgot-password">استعادة الحساب</a></p>
            <p style={{ marginTop: '10px', fontSize: '0.9rem', opacity: '0.7' }}>
              للمساعدة الفنية، تواصل مع <a href="mailto:support@example.com">الدعم الفني</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}